/**
 * Accueil tiles — registered as `extranet.homeQuickAccess` (fiches 22062 /
 * 22058, 22065, 22067, 22079): « Accès rapide » (Contrats n en cours,
 * Sinistres n en cours, Primes n à régler) and « Flotte automobile »
 * (Véhicules n en cours). Each tile is a clickable `Card` (icon, label,
 * `WS2_EA_CTR_*.NB` count, chevron) that opens its list screen.
 *
 * Configurable-ready:
 * - tiles render from the declarative {@link QUICK_ACCESS_TILES} (stable keys
 *   `contrats` / `sinistres` / `primes` / `vehicules`); `_layoutOverrides`
 *   item edits (rename / hide / reorder / tooltip — the `items` bucket, or
 *   `fields`, which is the bucket the rule engine fills for info items) are
 *   merged with `applyItemOverrides`;
 * - sections render from {@link QUICK_ACCESS_SECTIONS} (stable keys
 *   `accesRapide` / `flotte`, titles in config); `_layoutOverrides.sections`
 *   reorders them (`reorderSections`) and carries a forward-compatible
 *   `label`; the « Accès rapide » heading is the component title, resolved
 *   through `resolveCardTitle` (a `setTitle` rule renames it);
 * - the base config (items + sections + title) is published with
 *   `usePublishLayoutConfig` so the admin editor lists every target.
 *
 * Data: one React Query per tile (key = endpoint + registry + session), so a
 * slow or failing counter never blocks the others; a `Skeleton` while
 * loading, no count line on error. `counts` presets a tile's count (admin
 * preview) and skips its fetch.
 */

import { useMemo, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';

import { Icon, Text, Tooltip } from '@components/base/display';
import { Skeleton } from '@components/base/feedback';
import { Card, Grid, HStack, VStack } from '@components/base/layout';
import type { SectionConfig } from '@components/integrated/entity-form';
import {
  applyItemOverrides,
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
  type InfoItemDescriptor,
  type LayoutOverrides,
  type PublishedLayoutConfig,
} from '@components/shared/configurable';

import { fetchCounter, homeCounterQueryKey } from '../../api/homeService';
import { useExtranetAuthStore } from '../../auth/store';
import { extranetPath } from '../../navigation';
import { useSpaLink } from '../useSpaLink';
import {
  HOME_QUICK_ACCESS_ID,
  QUICK_ACCESS_SECTIONS,
  QUICK_ACCESS_TILES,
  QUICK_ACCESS_TITLE_SECTION,
  type QuickAccessSection,
  type QuickAccessTileDescriptor,
  type QuickAccessTileKey,
} from './quickAccessConfig';

/** A counter is considered fresh for a minute (same as the header bell). */
const COUNTER_STALE_TIME_MS = 60_000;

export interface QuickAccessTilesProps {
  /**
   * Preset counts by tile key (admin preview) — a preset tile shows this
   * value and does not fetch its counter.
   */
  counts?: Partial<Record<QuickAccessTileKey, number>>;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

/** Ascending `order`, declaration order on ties (stable sort). */
function byOrder<T extends { order?: number }>(a: T, b: T): number {
  return (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
}

interface QuickAccessTileProps {
  tile: QuickAccessTileDescriptor;
  /** Absolute path the tile opens. */
  href: string;
  presetCount: number | undefined;
}

function QuickAccessTile({ tile, href, presetCount }: Readonly<QuickAccessTileProps>) {
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const sessionId = useExtranetAuthStore((s) => s.sessionId);
  const navigate = useNavigate();
  const spaLink = useSpaLink();

  const query = useQuery<number>({
    queryKey: homeCounterQueryKey(tile.endpoint, registrySlug, sessionId),
    queryFn: () => fetchCounter(registrySlug, tile.endpoint),
    enabled: registrySlug !== null && presetCount === undefined,
    staleTime: COUNTER_STALE_TIME_MS,
    retry: false,
  });

  const loading = presetCount === undefined && query.isLoading;
  const count = presetCount ?? (query.isError ? undefined : query.data);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    void navigate(href);
  };

  const label: ReactNode = (
    <Text as="span" size="base" weight="semibold" truncate>
      {tile.label}
    </Text>
  );

  let countLine: ReactNode = null;
  if (loading) {
    countLine = <Skeleton variant="text" width="5rem" data-testid={`home-tile-loading-${tile.key}`} />;
  } else if (count !== undefined) {
    countLine = (
      <Text as="span" size="xs" color="muted">
        <Text
          as="span"
          size="xs"
          weight="medium"
          className="tabular-nums"
          data-testid={`home-tile-count-${tile.key}`}
        >
          {count}
        </Text>{' '}
        {tile.countLabel}
      </Text>
    );
  }

  return (
    <Card
      role="link"
      tabIndex={0}
      data-tile-key={tile.key}
      onClick={spaLink(href)}
      onKeyDown={handleKeyDown}
      className="h-full cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
    >
      <Card.Content>
        <HStack gap={3} align="center">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50">
            <Icon icon={tile.icon} size="md" color="primary" />
          </span>
          <VStack gap={0} className="min-w-0 flex-1">
            {tile.tooltip === undefined ? label : <Tooltip content={tile.tooltip}>{label}</Tooltip>}
            {countLine}
          </VStack>
          <Icon icon={ChevronRight} size="sm" color="muted" />
        </HStack>
      </Card.Content>
    </Card>
  );
}

/** Base (pre-override) title: the heading of the « Accès rapide » section. */
const BASE_TITLE =
  QUICK_ACCESS_SECTIONS.find((section) => section.key === QUICK_ACCESS_TITLE_SECTION)?.title ?? '';

/** Published base config: every tile (items), every section and the title. */
const PUBLISHED_ITEMS: InfoItemDescriptor[] = QUICK_ACCESS_TILES.map((tile) => ({
  key: tile.key,
  label: tile.label,
  order: tile.order,
  ...(tile.visible === undefined ? {} : { visible: tile.visible }),
  ...(tile.tooltip === undefined ? {} : { tooltip: tile.tooltip }),
}));
const PUBLISHED_SECTIONS: SectionConfig[] = QUICK_ACCESS_SECTIONS.map((section) => ({
  id: section.key,
  title: section.title,
  order: section.order,
}));
const PUBLISHED_CONFIG: PublishedLayoutConfig = {
  ...buildPublishedConfigFromInfoItems(PUBLISHED_ITEMS, BASE_TITLE),
  sections: PUBLISHED_SECTIONS,
};

/** A section ready to render: resolved title + its visible, ordered tiles. */
interface ResolvedSection extends QuickAccessSection {
  tiles: QuickAccessTileDescriptor[];
}

export function QuickAccessTiles({ counts, _layoutOverrides }: Readonly<QuickAccessTilesProps>) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  usePublishLayoutConfig(HOME_QUICK_ACCESS_ID, PUBLISHED_CONFIG);

  const itemOverrides = _layoutOverrides?.items ?? _layoutOverrides?.fields;
  const sectionOverrides = _layoutOverrides?.sections;
  const titleOverride = _layoutOverrides?.title;

  const sections = useMemo<ResolvedSection[]>(() => {
    const tiles = applyItemOverrides(QUICK_ACCESS_TILES, itemOverrides).filter(
      (tile) => tile.visible !== false,
    );
    return QUICK_ACCESS_SECTIONS.map((section) => {
      const override = sectionOverrides?.[section.key];
      const sectionTitle = override?.label ?? section.title;
      return {
        ...section,
        title:
          section.key === QUICK_ACCESS_TITLE_SECTION
            ? resolveCardTitle(sectionTitle, { title: titleOverride })
            : sectionTitle,
        order: override?.order ?? section.order,
        tiles: tiles.filter((tile) => tile.section === section.key).sort(byOrder),
      };
    })
      .filter((section) => section.tiles.length > 0)
      .sort(byOrder);
  }, [itemOverrides, sectionOverrides, titleOverride]);

  if (sections.length === 0) return null;

  return (
    <VStack gap={10} data-testid="extranet-home-quick-access">
      {sections.map((section) => (
        <VStack
          key={section.key}
          as="section"
          gap={4}
          aria-label={section.title}
          data-section-key={section.key}
        >
          <Text as="h2" size="xl" weight="semibold">
            {section.title}
          </Text>
          <Grid columns={section.columns} gap={3}>
            {section.tiles.map((tile) => (
              <QuickAccessTile
                key={tile.key}
                tile={tile}
                href={extranetPath(tenantSlug, tile.to)}
                presetCount={counts?.[tile.key]}
              />
            ))}
          </Grid>
        </VStack>
      ))}
    </VStack>
  );
}
